import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';

import { IconCopy, IconPlus, IconTrash } from '@tabler/icons-react';

import api from '../../../../services/api';

export default function AppKeys() {
    const [appId, setAppId] = useState(null);
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [status, setStatus] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const loadKeys = useCallback(async (currentAppId) => {
        try {
            const response = await api.get(`v1/apps/${currentAppId}/keys/`);
            setKeys(response.data?.keys || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar as chaves.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response = await api.get('v1/apps/default/');
                const id = response.data?.app?.id;
                setAppId(id || null);
                if (id) await loadKeys(id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar o App.' });
                setLoading(false);
            }
        })();
    }, [loadKeys]);

    const handleCreate = async () => {
        if (!appId) return;
        setCreating(true);

        try {
            await api.post(`v1/apps/${appId}/keys/`, {});
            setStatus({ type: 'success', message: 'Chave criada com sucesso.' });
            await loadKeys(appId);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao criar a chave.' });
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (key) => {
        const confirmDelete = window.confirm('Excluir esta chave? Qualquer integração usando ela vai parar de funcionar.');
        if (!confirmDelete) return;

        try {
            await api.delete(`v1/apps/${appId}/keys/${key.id}/`);
            setKeys((prev) => prev.filter((k) => k.id !== key.id));
            setStatus({ type: 'success', message: 'Chave excluída.' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir a chave.' });
        }
    };

    const handleCopy = async (key) => {
        try {
            await navigator.clipboard.writeText(key.key);
            setCopiedId(key.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao copiar a chave.' });
        }
    };

    return (
        <Box sx={{ maxWidth: 640 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Chaves da Aplicação
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Usadas por integrações e sistemas externos (ex: o agente de IA) pra autenticar chamadas
                server-to-server nesta conta, sem depender de login por email/senha.
            </Typography>

            {status && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
                    {status.message}
                </Alert>
            )}

            <List sx={{ mb: 2 }}>
                {keys.map((key) => (
                    <ListItem
                        key={key.id}
                        sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            mb: 1
                        }}
                        secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton onClick={() => handleCopy(key)} size="small">
                                    <IconCopy size={16} />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(key)} size="small" color="error">
                                    <IconTrash size={16} />
                                </IconButton>
                            </Box>
                        }
                    >
                        <ListItemText
                            primary={key.key}
                            secondary={copiedId === key.id ? 'Copiado!' : key.created_at}
                            primaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                        />
                    </ListItem>
                ))}

                {!loading && keys.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Nenhuma chave criada ainda.
                    </Typography>
                )}
            </List>

            <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleCreate} disabled={creating || !appId}>
                {creating ? 'Gerando...' : 'Gerar nova chave'}
            </Button>
        </Box>
    );
}
